import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { authService } from '@/services/auth/authService';
import { addNotification, NotificationSeverity } from '@/hooks/useActionCenter';
import { queryKeyRootForEntity } from '@/hooks/queryKeyRegistry.generated';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050';
const HUB_URL = `${API_BASE_URL}/signalr/notifications`;

interface UserNotificationMessage {
  title: string;
  description?: string;
  severity: NotificationSeverity;
  entityType?: string;
  entityId?: string;
  /** Other entity types the operation changed; the producer declares them (UserNotification). */
  affectedEntityTypes: string[];
}

const SEVERITIES: NotificationSeverity[] = ['success', 'error', 'warning', 'info'];

/** Normalize payload property casing (C# PascalCase vs camelCase). */
function normalizeMessage(raw: Record<string, unknown>): UserNotificationMessage {
  const severity = (raw.severity ?? raw.Severity ?? 'info') as string;
  return {
    title: (raw.title ?? raw.Title ?? '') as string,
    description: (raw.description ?? raw.Description) as string | undefined,
    severity: SEVERITIES.includes(severity as NotificationSeverity)
      ? (severity as NotificationSeverity)
      : 'info',
    entityType: (raw.entityType ?? raw.EntityType) as string | undefined,
    entityId: (raw.entityId ?? raw.EntityId) as string | undefined,
    affectedEntityTypes: toStringArray(raw.affectedEntityTypes ?? raw.AffectedEntityTypes),
  };
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/**
 * App-global subscriber for the user-notification channel (NotificationsHub).
 * Mounted once in Layout: real-time push into the Notifications bell
 * (useActionCenter store) — no polling. Also refreshes the cached views of every
 * entity the operation changed, so a page left open shows the outcome instead of
 * the state from before the run (e.g. a publish → the version and its snapshot).
 */
export function useNotificationsSignalR() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    let stopped = false;

    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => authService.getToken() || '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    const handleNotificationPublished = (raw: Record<string, unknown>) => {
      const message = normalizeMessage(raw);
      addNotification({
        title: message.title,
        description: message.description,
        severity: message.severity,
        entityType: message.entityType,
        entityId: message.entityId,
      });

      // The notification's own entity plus whatever the producer says it changed. Both are
      // entity NAMES; the generated registry turns each into its hooks' root query key, so
      // this hook knows no entity and a new run type needs no edit here.
      for (const entityType of [message.entityType, ...message.affectedEntityTypes]) {
        const root = entityType && queryKeyRootForEntity(entityType);
        if (root) queryClient.invalidateQueries({ queryKey: [root] });
      }
    };

    connection.on('NotificationPublished', handleNotificationPublished);

    const subscribe = () => connection.invoke('Subscribe').catch((err: unknown) => {
      console.error('[NotificationsSignalR] Subscribe failed:', err);
    });

    // Groups don't survive a reconnect (new connection id) — re-subscribe.
    connection.onreconnected(() => {
      subscribe();
    });

    // Defer the connect by a tick — see useTaskInboxSignalR: StrictMode's throwaway first
    // mount would otherwise open a socket only to abort its own negotiation, which the SignalR
    // client reports as a failed connection on every reload in development.
    const startTimer = setTimeout(() => {
      connection
        .start()
        .then(() => {
          if (stopped) return;
          console.log('[NotificationsSignalR] Connected to', HUB_URL);
          subscribe();
        })
        .catch((err) => {
          // A real teardown mid-negotiation (navigating away) is our own doing, not a failure.
          if (stopped) return;

          console.error('[NotificationsSignalR] Connection failed:', err);
        });
    }, 0);

    return () => {
      stopped = true;
      clearTimeout(startTimer);
      connection.off('NotificationPublished', handleNotificationPublished);
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
    };
  }, [queryClient]);
}
